<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { AdditionalData, DateKeys, NewUser } from '@/types';
import { useStore } from 'vuex';
import { formatName, makeFirstLetterUpper } from '@/utils/main';
import VitaVacationType from '@/components/VitaVacationType.vue';
import { useTableScrollX } from '@/utils/scroll';
import { generatePDF } from '@/utils/print';

const { tableScroll, scrollEnd, scrollStart, scrollSmoothStart, scrollSmoothStop } = useTableScrollX();

const store = useStore<Storage>()
const users = computed<NewUser[]>(() => {
  return store.getters["data/pharmUsers"];
});

const currentMouth = computed<string>(() => {
  return store.getters['getCurrentMoth']
})

const currentDate = computed<Date>(() => {
  return store.getters['getCurrentDate']
})

const days = computed<string[]>(() => {
  return store.getters['getCurrentDays']
})

const dateArr = computed<DateKeys[]>(() => {
  return store.getters['data/timeScheduleKeys']
})

const isVacationOnDay = (timeShift: object | AdditionalData, item: DateKeys) => {
  if (!timeShift) return
  return (timeShift[item.key] && (timeShift[item.key][0] && !timeShift[item.key][0].zupGuid))
}

const tableTitle = computed<string>(() => {
  return `График ${makeFirstLetterUpper(currentMouth.value)} ${currentDate.value.getFullYear()}`
})

const pdfContent: Ref<HTMLElement | null> = ref(null);
const btnPrintDisabled = ref(false)

const onClickPrintBtn = async () => {
  btnPrintDisabled.value = true
  if (!pdfContent.value) return;

  try {
    await generatePDF(pdfContent.value)
  } catch (e) {
    console.error(e);
  } finally {
    btnPrintDisabled.value = false
  }
}
</script>

<template>
  <div class="print-table-wrap">
    <div class="print-table-btn">
      <vita-button color="blue" width="print-btn-action" @click.prevent="onClickPrintBtn" :disabled="btnPrintDisabled"
                   data-qa="print-table-button-print">
        <span>Напечатать</span>
        <vita-icon class="btn-elements-icon" name="print"/>
      </vita-button>
    </div>
    <perfect-scrollbar ref="tableScroll">
      <div class="print-table" id="printableArea" ref="pdfContent">
        <table class="main-table">
          <caption>
            <div class="table-title">
              <h2>{{tableTitle}}</h2>
            </div>
          </caption>
          <thead>
          <tr>
            <th></th>
            <th v-for="(item, index) in days" :key="item + index" :data-day="item">
              {{makeFirstLetterUpper(item)}}
            </th>
          </tr>
          <tr>
            <th>ФИО</th>
            <th v-for="(item, index) in days" :key="item + index" :data-day="item">
              {{index + 1}}
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(user) in users" :key="user.zupGuid">
            <td style="height: 32px; overflow: hidden">
              <div class="table-user">

                <div class="table-user__name">{{formatName(user.name)}}</div>
                <div class="table-user__info">
                  <div class="table-user__info-col">
                    <div class="table-user__position">{{user.positionCode}}</div>
                    <div class="table-user__bid">({{user.bid}})</div>
                  </div>
                  <div v-if="user.frv && user.frv.frvNorm">{{user.frv ? user.frv.frvNorm : null}}ч</div>
                </div>
              </div>
            </td>

            <template v-if="user.dateArr">
              <template v-for="(item, position) in user.dateArr" :key="item.key + user.id">
                <td :data-big="!!(user.timeShift && user.timeShift[item.key] && user.timeShift[item.key].some(time => time.big))"
                    :class="{ dayOut: dateArr[position].dayOut }">
                  <template v-if="user.timeShift">
                    <div v-if="isVacationOnDay(user.timeShift, item)">
                      <vita-vacation-type
                        :type="user.timeShift[item.key]"
                        :user="user"
                        :item="item" />
                    </div>

                    <template v-else>
                      <template v-for="(time) in user.timeShift[item.key]" :key="`T-${time.id}`">
                        <div class="table-item-wrap" :class="{'item-big': time.big}"
                             v-if="time.status !== 'Canceled' && time.status !== 'MissAccepted'">
                          <div class="print-table-item">
                            <div>{{ time.timeFrom }}</div>
                            <div>{{ time.timeTo }}</div>
                          </div>
                        </div>
                      </template>
                    </template>

                  </template>
                </td>
              </template>
            </template>
          </tr>
          </tbody>
        </table>
      </div>
    </perfect-scrollbar>
    <vita-icon
      class="scroll-arrow"
      data-qa="print-table-icon-scroll-start"
      @click="scrollStart"
      @mousedown.prevent="scrollSmoothStart(-1)"
      @mouseup="scrollSmoothStop"
      @mouseout="scrollSmoothStop"
      name="chevron-left"
    />
    <vita-icon
      class="scroll-arrow scroll-arrow-right"
      data-qa="print-table-icon-scroll-end"
      @click="scrollEnd"
      @mousedown.prevent="scrollSmoothStart(1)"
      @mouseup="scrollSmoothStop"
      @mouseout="scrollSmoothStop"
      name="chevron-left"
    />
  </div>
</template>

<style lang="scss" scoped>
td[data-big=true] + td:has(.table-item-wrap) .table-item-wrap {
  border-bottom: unset;
}

td[data-big=true] + td:nth-child(even)[data-big=true] .table-item-wrap {
  margin-top: -32px;
  border-top: unset;
}

td[data-big=true] + td:has(.item-big) + td[data-big=true]::before {
  display: none;
}

td[data-big=true]:has(::before) {
  display: none;
}

td[data-big=true] + td[data-big=true] .table-item-wrap {
  margin-top: 101%;
  border-top: 1px solid rgb(144, 144, 144);
}

td[data-big=true] + td[data-big=false] .table-item-wrap {
  margin-bottom: 32px;
}

td[data-big=true]:not(:has(+ td[data-big=true])) .table-item-wrap {
  margin-top: 0;
}

td[data-big=true]:not(:has(+ td[data-big=true])) .table-item-wrap {
  border-bottom: unset;
}

td[data-big=true] + td[data-big=true]:nth-child(odd):not(:has(+ td[data-big=true])) .table-item-wrap {
  margin-top: 32px!important;
}

td[data-big=false] + td[data-big=true]:nth-child(odd):has(+ td[data-big=true]) .table-item-wrap {
  margin-bottom: 0;
  margin-top: 32px!important;
  border-bottom: unset;
}

// expand shift
.table-item-wrap.item-big {
  margin-right: -104%;
  margin-top: -100%;
  border-bottom: 1px solid rgba(144, 144, 144, 1);
  position: relative;
  z-index: 1;
  box-sizing: content-box;
}

//
.print-table {
  max-width: 1220px;
  margin: 0 auto;
  position: relative;
  
  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    font-style: normal;
    font-weight: 700;
    font-size: 13px;
    line-height: 16px;
    text-align: center;
    color: #333333;
  }

  th, td {
    border: 1px solid #909090;
    text-align: center;
    min-width: 32px;
    min-height: 32px;

    &.dayOut {
      background: rgb(233, 233, 233);
    }
  }

  th {
    height: 32px;
    width: 32px;
  }

  th[data-day='сб'],
  th[data-day='вс'] {
    background: rgb(233, 233, 233);
    position: relative;
  }

  td {
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 12px;
    text-align: center;
    color: #333333;
    height: 33px;
    max-height: 33px;
    position: relative;

    &.dayOut {

      .table-item-wrap {
        background: rgb(233, 233, 233);
      }

      .table-item-wrap.item-big {
        background: rgb(233, 233, 233);
      }
    }
  }

  .table-item-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: white;
    min-height: 32px;
    position: relative;
  }

  .print-table-item {
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 12px;
    color: #333333;
  }

  .item-big .print-table-item {
    display: block;
  }

  .item-big .print-table-item > div {
    display: inline-block;

    &:first-child::after {
      content: '-';
    }
  }

  & tr > td:first-child,
  & tr > th:first-child {
    min-width: 211px;
    text-align: left;
  }

  & tr > td:first-child {
    padding: 4px;
  }

  & tr > th:first-child {
    padding: 6.5px 8px;
  }

  .table-title {
    background: rgb(233, 233, 233);
    border: 1px solid #919191;
    border-bottom-width: 0;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;

    h2 {
      font-style: normal;
      font-weight: 600;
      font-size: 22px;
      line-height: 27px;
      color: #333333;
    }
  }
}

.table-user__name {
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 100%;
  text-align: left;
  color: #333333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.table-user__position {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 144px;
}

.table-user__info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-style: normal;
  font-weight: 500;
  font-size: 9px;
  line-height: 11px;
  color: #757575;
}

.table-user__info-col {
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-table-btn {
  position: absolute;
  top: 13px;
  right: 15px;
  z-index: 1;
}

.print-table-wrap {
  position: relative;

  .print-table {
    padding-bottom: 24px;
  }
}

.scroll-arrow {
  position: absolute;
  bottom: 0;
  background: #FFFFFF;
}

.scroll-arrow-right {
  right: 0;
  transform: rotate(180deg);
}
</style>